import type {
  MatrixFocusTarget,
  UiEffectContextForKey,
  UiEffectRegistry,
  UiEffectRegistryKey,
} from './types';
import { historyAction as historyActions } from 'flow/service/history/constants';
import type { HistoryAction } from 'flow/service/history/constants';
import type {
  DataRouteSubEditorArgs,
  ExpressionUiEffectContext,
  ThresholdSubEditorArgs,
  WorkflowHistoryEntry,
} from 'flow/store/redux/types';
import {
  undoUiEffectMap,
  WORKFLOW_HISTORY_HIGHLIGHT_ATTRIBUTE,
} from './constants';
import type { DataRoute, Threshold } from 'flow/store/engine/types';
import type { Axis } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/AxisHeaderDropdown';
import type { FormKey } from 'flow/editor/components/NodeEditorDrawer/useNodeEditorForm/types';
import isEqual from 'lodash/isEqual';

export function getDatarouteHtmlElementId(
  target: DataRoute['publicId'] | DataRoute['category'],
) {
  return `dataroute-row-${target}`;
}

export function getRuleHtmlElementId(
  rulePublicId: Threshold['publicId'],
) {
  // todo: add threshold-side
  return `rule-content-${rulePublicId}`;
}

export function getExpressionHtmlElementId(
  expressionPath: ExpressionUiEffectContext['expressionPath'],
) {
  return `expression-content-${CSS.escape(expressionPath)}`;
}

export function getMatrixValueHtmlElementId(target: MatrixFocusTarget): string {
  const { cellPosition, configPopover, axisDataroute } = target as {
    cellPosition?: { row: number; col: number };
    configPopover?: Axis;
    axisDataroute?: Axis;
  };

  if (cellPosition) {
    return `matrix-cell-${cellPosition.row}-${cellPosition.col}`;
  }
  if (configPopover) {
    return `matrix-config-popover-${configPopover}-axis`;
  }
  if (axisDataroute) {
    return `matrix-value-dataroute-${axisDataroute}-axis`;
  }

  return '';
}

export function getMLAHtmlElementId(
  mlaElement: (
    // attribute token of the mla attribute
    Threshold['left']['attributeToken']
    | Threshold['right']['attributeToken']
    | DataRoute['attribute']['token']
    // or the filters dropdown element
    | 'filters'
  ),
) {
  return `mla-content-${mlaElement}`;
}


export function getNodeEditorFormHtmlElementId(formKey: FormKey, field: string) {
  return `node-editor-form-${formKey}-${field}`;
}

/**
 * Empty has more than one spelling across a form and the data behind it, so comparing
 * values directly reports untouched fields as changed.
 *  - For example: When JQ Formula is disabled, react-hook-form submits it as `undefined`
 *    instead of its `''` source value, so it would always be highlighted.
 * Collapsing empty against empty never hides a real edit, because a real edit
 * always leaves one side non-empty.
 */
const isEmptyFieldValue = (value: unknown): boolean => (
  value === undefined || value === null || value === ''
  || (Array.isArray(value) && value.length === 0)
);

function hasFieldChanged(current: unknown, source: unknown): boolean {
  if (isEmptyFieldValue(current) && isEmptyFieldValue(source)) return false;
  return !isEqual(current, source);
}

/**
 * Maps the fields a form changed to the ids of the elements that should highlight, for
 * drawer tabs that apply a whole form as one history commit. The caller anchors on the
 * first id and passes the rest as related ids.
 */
export function getChangedFormFieldElementIds<TFormState extends Record<string, unknown>>({
  formKey,
  current,
  source,
  toWrapperField = field => field,
}: {
  formKey: FormKey;
  current: TFormState;
  source: TFormState;
  /** Fields rendering as one row report the id of whichever field owns their wrapper. */
  toWrapperField?: (field: string) => string;
}): string[] {
  const changedIds = new Set<string>();

  Object.keys(current).forEach(field => {
    if (hasFieldChanged(current[field], source[field])) {
      changedIds.add(getNodeEditorFormHtmlElementId(formKey, toWrapperField(field)));
    }
  });

  return [...changedIds];
}

export function clearWorkflowHistoryFieldHighlights() {
  const historyHighlightedElements = document.querySelectorAll(
    `[${WORKFLOW_HISTORY_HIGHLIGHT_ATTRIBUTE}='true']`,
  );

  historyHighlightedElements.forEach(element => {
    element.removeAttribute(WORKFLOW_HISTORY_HIGHLIGHT_ATTRIBUTE);
  });
}

export function getUiEffectKey(
  historyEntry: WorkflowHistoryEntry,
  historyAction: HistoryAction,
): UiEffectRegistryKey {
  if (historyAction === historyActions.undo) {
    // When we undo a "remove" operation - it is akin to an "add" operation
    // and vice versa for "add" and "remove" operations
    const operation = undoUiEffectMap[historyEntry.operation];
    return `${operation}:${historyEntry.uiEffectContext.target}`;
  }

  return `${historyEntry.operation}:${historyEntry.uiEffectContext.target}`;
}

/**
 * Runs the handler registered for `key` with `ctx`. The generic `K` ties the
 * looked-up handler to the context type, so the invocation type-checks without
 * a cast (a correlated-union lookup).
 */
export function runUiEffect<K extends UiEffectRegistryKey>(
  uiEffectRegistry: UiEffectRegistry,
  key: K,
  ctx: UiEffectContextForKey<K>,
): void {
  uiEffectRegistry[key]?.(ctx);
}

export function isThresholdSubEditorArgs(
  args: ThresholdSubEditorArgs | DataRouteSubEditorArgs,
): args is ThresholdSubEditorArgs {
  return 'threshold' in args;
}

